import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Plus, Loader2, Copy, Link2, ChevronRight, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ListSkeleton } from '../components/skeletons';
import RegenerateCodeModal from '../components/RegenerateCodeModal';
import GroupsLoadError from '../components/GroupsLoadError';
import {
  codeExpiryLabel, copyDeFalloDeGrupo, copyText, isCodeExpired,
  joinLinkFor, conteoDeAlumnos,
} from '../lib/groups';
import useCodeExpiryTick from '../hooks/useCodeExpiryTick';
import BuscadorEnTarjeta from '../components/BuscadorEnTarjeta';
import { coincide } from '../lib/buscarPorNombre';
import { useAnchoDeCaja, esAngosto } from '../hooks/useAnchoDeCaja';

// ajustes-1-3-0 (Bloque F1, #502): por debajo de este ancho del cuadro de buscar, su
// placeholder pasa a la forma corta ("Buscar grupo"). Se mide la caja, no la ventana.
export const UMBRAL_BUSCADOR_CORTO = 200;

/* PILOTO-1 (firma #79) — LA PANTALLA DE PROFESOR: la lista de grupos.
 *
 * DÓNDE VIVE: ruta `/groups`, dentro de `AppLayout` y detrás de
 * `<InstitutionalRoute>` (App.js). Una cuenta SIN bandera nunca llega: el guard
 * la manda a /dashboard antes de montar nada, el renglón del menú no existe
 * para ella, y aunque teclee la URL el servidor le contesta 403 de todas formas.
 * Dos candados independientes, ninguno confía en el otro.
 *
 * SAFE AREAS: esta pantalla NO escribe ni un `env(safe-area-inset-*)`, y es a
 * propósito. Vive dentro del scrollport de `AppLayout`, que ya lleva el inset de
 * arriba en su `mt-[calc(...)]` y el colchón de abajo en `CONTENT_CLEARANCE_CLASS`;
 * escribir otro aquí contaría el notch DOS VECES — la lección que Analytics.js:737
 * dejó firmada. Los MODALES sí llevan el suyo: son `fixed` al viewport y no
 * heredan nada (patrón Dashboard.js:2994).
 *
 * DESKTOP DE PRIMERA, no un móvil estirado: la lista es una rejilla que crece a
 * 2 y 3 columnas, el ancho está topado para que las tarjetas no queden
 * kilométricas en 27", y el hover solo existe donde hay puntero (`glass-card`,
 * regla R-3 de index.css). Tap targets de 44pt mínimo en todo lo que se toca.
 */

export default function GroupsPage() {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);   // null = aún no se sabe
  // PILOTO-1C — EL TERCER ESTADO. «No pude leer» ya no se disfraza de «no tienes
  // grupos»: eran dos hechos distintos pintados con la misma frase, y la frase
  // era la falsa. Ver components/GroupsLoadError.js.
  const [errorDeCarga, setErrorDeCarga] = useState(false);
  const [limits, setLimits] = useState({ max_groups: 0, max_members: 0 });
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  // PILOTO-1B: el grupo cuyo código se está a punto de rotar, y si ya salió.
  const [confirmRegen, setConfirmRegen] = useState(null);
  // El ID que se está rotando, no un booleano: con un booleano compartido, una
  // rotación en vuelo dejaba el botón «Generar» del SIGUIENTE grupo deshabilitado.
  // Mismo patrón que `leaving` en JoinGroupCard.
  const [regeneratingId, setRegeneratingId] = useState('');
  // ajustes-1-3-0 (Bloque F1, #502): el cuadro de buscar grupos por nombre (misma
  // normalizacion que las tarjetas del Panel: sin acentos ni mayusculas) y la caja que
  // se mide para acortar su placeholder.
  const [busqueda, setBusqueda] = useState('');
  const cajaDelBuscador = useRef(null);
  const anchoDelBuscador = useAnchoDeCaja(cajaDelBuscador);
  const gruposVisibles = (groups || []).filter((g) => coincide(g.name, busqueda));

  const load = useCallback(async () => {
    setErrorDeCarga(false);
    try {
      const res = await api.get('/groups');
      setGroups(res.data.groups || []);
      setLimits(res.data.limits || { max_groups: 0, max_members: 0 });
    } catch (err) {
      // NI LISTA VACÍA NI TOAST FUGAZ. La lista se deja en `null` —o sea: no se
      // sabe— y la pantalla pinta su estado de error con «Reintentar». Un toast
      // que se va solo a los cinco segundos deja al profesor mirando una
      // pantalla que le miente, y el reintento vivía únicamente en el F5.
      setGroups(null);
      setErrorDeCarga(true);
    }
  }, []);

  // EL FETCH ES DE MONTAJE. PILOTO-1C sacó a `t` del cierre de `load` —el copy
  // del fallo ya no se arma ahí, lo pinta `GroupsLoadError`— así que `load` es
  // de verdad estable (deps vacías) y el efecto ya no necesita silenciar la
  // regla para acotar su disparador: la declara honesta. El motivo por el que
  // `t` no podía entrar sigue vigente y por eso salió: no está garantizado
  // estable entre renders, y como dependencia re-disparaba la lectura en cada
  // uno, pisando con una respuesta vieja la mutación recién aplicada al estado.
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e?.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await api.post('/groups', { name });
      setGroups(prev => [res.data.group, ...(prev || [])]);
      setNewName('');
      setShowCreate(false);
      toast.success(t('groups.toasts.created'));
    } catch (err) {
      // PILOTO-1C — el 400 del nombre sale en cristiano y en el idioma del
      // profesor. El servidor contesta «Group name must be at most 60
      // characters» (services/groups.py, la MISMA función para crear y para
      // renombrar), que era la única frase de esta pantalla que se le pintaba al
      // usuario en inglés. El 403 del sello tiene su propio copy.
      toast.error(copyDeFalloDeGrupo(err, t, 'dashboard:groups.toasts.create_failed',
        { 400: 'name_invalid' }));
    } finally { setCreating(false); }
  };

  const copy = async (value, okKey) => {
    // UNA sola llamada: copiar dos veces para decidir el toast dispararía dos
    // escrituras al portapapeles y, en el respaldo del textarea, dos nodos.
    const ok = await copyText(value);
    if (ok) toast.success(t(okKey));
    else toast.error(t('groups.toasts.copy_failed'));
  };

  // PILOTO-1B — REGENERAR. La respuesta trae el grupo ENTERO ya recalculado por
  // el servidor (código, token y veredicto de vencimiento), así que se reemplaza
  // en su sitio en vez de re-leer la lista: una petición, no dos, y la tarjeta
  // no parpadea.
  const regenerate = async () => {
    // EL OBJETIVO SE CAPTURA ANTES DEL AWAIT. `confirmRegen` es estado, y leerlo
    // DESPUÉS de la petición usa el valor de este render: si el profesor cancela
    // en vuelo y abre el diálogo de OTRO grupo, ese cierre y ese toast caerían
    // sobre el grupo equivocado y le dirían «listo» de algo que no rotó.
    const objetivo = confirmRegen;
    if (!objetivo) return;
    setRegeneratingId(objetivo.id);
    try {
      const res = await api.post(`/groups/${objetivo.id}/regenerate_code`);
      setGroups(prev => (prev || []).map(g => (g.id === objetivo.id ? res.data.group : g)));
      // Solo se cierra el diálogo SI sigue siendo el de este grupo.
      setConfirmRegen(prev => (prev && prev.id === objetivo.id ? null : prev));
      toast.success(t('groups.toasts.regenerated'));
    } catch (err) {
      toast.error(copyDeFalloDeGrupo(err, t, 'dashboard:groups.toasts.regenerate_failed'));
    } finally { setRegeneratingId(''); }
  };

  // PILOTO-1B: cuando pase la hora de un vencimiento, la tarjeta se pone en
  // «Vencido» sola. Sin esto, la capa de reloj de `isCodeExpired` no se
  // evaluaba nunca en una pestaña quieta (ver hooks/useCodeExpiryTick).
  const transcurrido = useCodeExpiryTick((groups || []).map(g => g.code_expires_in));

  const atLimit = groups !== null && limits.max_groups > 0 && groups.length >= limits.max_groups;

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in-up" data-testid="groups-page">
      {/* Encabezado */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Users className="w-6 h-6 text-[#FF6B00]" /> {t('groups.title')}
        </h1>
        <p className="text-sm text-[#6a6a6a] mt-1">{t('groups.subtitle')}</p>
      </div>

      {/* ajustes-1-3-0 (Bloque F1, #502): el boton baja de la esquina a este renglon, en
          todos los anchos, y a su derecha va el cuadro de buscar (el de las tarjetas
          plegables del Panel). SIEMPRE lado a lado: sin salto de renglon, el boton con su
          ancho natural y el cuadro con lo que sobra (Carlos: "mejor no recortar el cuadro
          y ya para que no brinque"). El cuadro trae un margen de abajo propio para las
          tarjetas; aqui su caja lo descuenta para que el centrado del renglon sea el del
          campo y no el del campo mas su margen.
          remates-1-3-0c (Q4, W8): el boton y el cuadro miden lo MISMO, 44 px. El cuadro se
          pide con `alto={44}` (campo de 44 y caja sin margen de abajo), asi que la envoltura
          ya no descuenta nada: antes el boton media 44 y la caja 48. */}
      <div className="flex items-center gap-3" data-testid="groups-acciones">
        <Button
          onClick={() => setShowCreate(true)}
          disabled={atLimit}
          className="bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl min-h-[44px] px-4 text-sm disabled:opacity-40 shrink-0"
          data-testid="groups-create-btn">
          <Plus className="w-4 h-4 mr-2" /> {t('groups.create_btn')}
        </Button>
        <div ref={cajaDelBuscador} className="flex-1 min-w-0" data-testid="groups-buscar-caja">
          <BuscadorEnTarjeta t={t} valor={busqueda} alCambiar={setBusqueda} testid="groups-buscar" alto={44}
            placeholder={t(esAngosto(anchoDelBuscador, UMBRAL_BUSCADOR_CORTO)
              ? 'groups.buscar_placeholder_corto' : 'groups.buscar_placeholder')} />
        </div>
      </div>

      {atLimit && (
        <p className="text-xs text-amber-400" data-testid="groups-limit-notice">
          {t('groups.limit_reached', { max: limits.max_groups })}
        </p>
      )}

      {/* Lista — TRES estados, no dos: no se sabe (esqueleto), no se pudo leer
          (error + Reintentar) y vacía de verdad (el empty state de siempre). */}
      {errorDeCarga ? (
        <GroupsLoadError onRetry={load} testid="groups-error" />
      ) : groups === null ? (
        <ListSkeleton rows={3} testid="skeleton-groups" />
      ) : groups.length === 0 ? (
        <div className="glass-card rounded-[20px] p-8 md:p-12 text-center" data-testid="groups-empty">
          <Users className="w-10 h-10 text-[#333] mx-auto mb-4" />
          <p className="text-sm text-[#a0a0a0]">{t('groups.empty_title')}</p>
          <p className="text-xs text-[#555] mt-2 max-w-md mx-auto">{t('groups.empty_hint')}</p>
        </div>
      ) : gruposVisibles.length === 0 ? (
        /* ajustes-1-3-0 (Bloque F1, #502): con grupos pero sin coincidencia, T13 en el
           lugar de la lista. */
        <p className="text-sm text-[#6a6a6a]" data-testid="groups-buscar-vacio">{t('groups.buscar_vacio')}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="groups-list">
          {gruposVisibles.map((g) => {
            // PILOTO-1B — el veredicto lo da el servidor (`code_expired`); esto
            // solo lo lee, una vez por tarjeta.
            const vencido = isCodeExpired(g, transcurrido);
            // PILOTO-1C — CARD COMPACTA (indicación de Carlos): el aire vertical
            // baja (p-5 → p-4, gap-4 → gap-3) sin tocar un solo tap target — los
            // botones siguen en 44 pt, que es la regla móvil del repo y no se
            // negocia por estética. Lo que se recorta es el colchón, no lo que
            // se toca.
            return (
            <div key={g.id} className="glass-card rounded-[20px] p-4 flex flex-col gap-3"
              data-testid={`group-card-${g.id}`}>
              <button
                onClick={() => navigate(`/groups/${g.id}`)}
                className="text-left min-h-[44px] group/open"
                data-testid={`group-open-${g.id}`}>
                <span className="flex items-center justify-between gap-2">
                  {/* Elipsis y no `break-words`: un nombre de 60 caracteres
                      estiraba la tarjeta y rompía la altura pareja de la rejilla. */}
                  <span className="min-w-0 text-base font-semibold text-white truncate">{g.name}</span>
                  <ChevronRight className="w-4 h-4 text-[#555] shrink-0 transition-transform group-hover/open:translate-x-0.5" />
                </span>
                <span className="block text-xs text-[#6a6a6a] mt-1">
                  {conteoDeAlumnos(t, g.member_count)}
                </span>
              </button>

              {/* PILOTO-1B — EL CÓDIGO, CON SU RELOJ.
                  Vigente: naranja, con la hora local a la que muere y los dos
                  botones de copiar; regenerar es la acción SECUNDARIA.
                  Vencido: atenuado y tachado, copiar DESHABILITADO —un botón que
                  reparte una llave muerta es peor que no tenerlo— y regenerar
                  pasa a ser la acción PRINCIPAL, que es lo único que queda por
                  hacer ahí. */}
              <div className="bg-[#0d0d0d] border border-[#262626] rounded-xl p-3 space-y-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <p className="text-[10px] uppercase tracking-wider text-[#555]">{t('groups.code_label')}</p>
                    {vencido && (
                      <span className="text-[10px] uppercase tracking-wider text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5"
                        data-testid={`group-code-expired-${g.id}`}>
                        {t('groups.code_expired_badge')}
                      </span>
                    )}
                  </div>
                  <p className={`font-mono text-xl tracking-[0.25em] ${vencido ? 'text-[#a0a0a0] line-through' : 'text-[#FF6B00]'}`}
                    data-testid={`group-code-${g.id}`}>
                    {g.join_code}
                  </p>
                  <p className="text-[11px] text-[#6a6a6a] mt-1.5" data-testid={`group-code-expiry-${g.id}`}>
                    {vencido ? t('groups.code_expired_hint') : codeExpiryLabel(g, t, i18n?.language)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" disabled={vencido}
                    onClick={() => copy(g.join_code, 'groups.toasts.code_copied')}
                    className="border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] rounded-xl min-h-[44px] text-xs flex-1 disabled:opacity-40"
                    data-testid={`group-copy-code-${g.id}`}>
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> {t('groups.copy_code_btn')}
                  </Button>
                  <Button size="sm" variant="outline" disabled={vencido}
                    onClick={() => copy(joinLinkFor(g.join_code), 'groups.toasts.link_copied')}
                    className="border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] rounded-xl min-h-[44px] text-xs flex-1 disabled:opacity-40"
                    data-testid={`group-copy-link-${g.id}`}>
                    <Link2 className="w-3.5 h-3.5 mr-1.5" /> {t('groups.copy_link_btn')}
                  </Button>
                </div>
                <Button size="sm" variant={vencido ? 'default' : 'outline'}
                  onClick={() => setConfirmRegen(g)}
                  className={vencido
                    ? 'w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl min-h-[44px] text-xs'
                    : 'w-full border-[#333] text-[#6a6a6a] hover:text-white hover:border-[#555] rounded-xl min-h-[44px] text-xs'}
                  data-testid={`group-regenerate-${g.id}`}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> {t('groups.regenerate_btn')}
                </Button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <RegenerateCodeModal
        open={Boolean(confirmRegen)}
        groupName={confirmRegen?.name}
        expired={Boolean(confirmRegen) && isCodeExpired(confirmRegen, transcurrido)}
        onCancel={() => setConfirmRegen(null)}
        onConfirm={regenerate}
        working={Boolean(confirmRegen) && regeneratingId === confirmRegen.id} />

      {/* Modal de creación. `fixed` al viewport → sí escribe su propia safe area
          abajo (patrón Dashboard.js:2994); el tope arranca bajo el header. */}
      {showCreate && (
        <div className="fixed top-14 inset-x-0 bottom-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          data-testid="groups-create-modal">
          <div className="relative bg-[#111] border border-[#262626] rounded-[20px] p-6 sm:p-8 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] max-w-md w-full mx-4 shadow-2xl">
            <button onClick={() => { setShowCreate(false); setNewName(''); }}
              className="absolute top-4 right-4 min-h-[44px] min-w-[44px] flex items-center justify-center text-[#555] hover:text-white"
              data-testid="groups-create-close">
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-white mb-2">{t('groups.create_title')}</h3>
            <p className="text-sm text-[#6a6a6a] mb-5">{t('groups.create_hint')}</p>
            <form onSubmit={create} className="space-y-4">
              <Input
                autoFocus value={newName} onChange={e => setNewName(e.target.value)}
                maxLength={60} placeholder={t('groups.name_placeholder')}
                className="bg-[#0e0e0e] border-[#262626] text-white rounded-xl min-h-[44px]"
                data-testid="groups-create-input" />
              {/* post-t7-2b (A1, #613): lo que acepta el profe al crear, debajo del campo del nombre. */}
              <p className="text-xs text-[#a0a0a0] leading-relaxed" data-testid="aviso-crear-grupo">
                {t('groups.leg_crear')}
              </p>
              <Button type="submit" disabled={creating || !newName.trim()}
                className="w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl min-h-[44px] text-sm disabled:opacity-40"
                data-testid="groups-create-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t('groups.create_btn')}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
